/**
 * Domain Polling Hook
 * 
 * Polls domain provisioning status from backend API.
 * Used during club creation to show real-time status updates.
 * 
 * Features:
 * - Auto-polling while status is 'pending'
 * - Stops when status changes to 'ready' or 'failed'
 * - Configurable poll interval (default 5s)
 * - Error handling and retry logic
 * - Callback support for status updates
 */

import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Hook to poll domain provisioning status
 * 
 * @param {string} club_id - Club/Tenant ID
 * @param {string} domainId - Domain mapping ID (returned from create_club response)
 * @param {object} options - Configuration options
 * @param {number} options.interval - Poll interval in ms (default 5000)
 * @param {boolean} options.autoStart - Start polling immediately (default true)
 * @param {function} options.onStatusChange - Callback when status changes
 * @param {function} options.onSuccess - Callback when status becomes 'ready'
 * @param {function} options.onError - Callback on polling error
 * 
 * @returns {object} Polling state and controls
 *   - status: Current domain status ('pending'|'ready'|'failed')
 *   - sslStatus: SSL certificate status ('provisioning'|'ready'|'failed')
 *   - instructions: Array of setup instructions
 *   - dnsRecords: Array of DNS records to configure
 *   - loading: Is currently polling
 *   - error: Error message if polling failed
 *   - lastCheck: Timestamp of last status check
 *   - startPolling: Function to start polling
 *   - stopPolling: Function to stop polling
 *   - retry: Function to retry failed polling
 */
export function useDomainPolling(club_id, domainId, options = {}) {
  const {
    interval = 5000,
    autoStart = true,
    onStatusChange = null,
    onSuccess = null,
    onError = null,
  } = options;

  // State
  const [status, setStatus] = useState('pending');
  const [sslStatus, setSslStatus] = useState('provisioning');
  const [instructions, setInstructions] = useState([]);
  const [dnsRecords, setDnsRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastCheck, setLastCheck] = useState(null);
  const [previousStatus, setPreviousStatus] = useState(null);

  // Refs for cleanup
  const pollIntervalRef = useRef(null);
  const isPollingRef = useRef(false);
  const failureCountRef = useRef(0);

  /**
   * Fetch current domain status from API
   */
  const fetchDomainStatus = useCallback(async () => {
    if (!club_id || !domainId) {
      console.warn('useDomainPolling: Missing club_id or domainId');
      return null;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch(
        `/api/clubs/${club_id}/domains/poll/${domainId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include', // Include auth cookies
        }
      );

      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      // Update state
      setStatus(data.status || 'pending');
      setSslStatus(data.ssl_status || 'provisioning');
      setInstructions(data.instructions || []);
      setDnsRecords(data.dns_records || []);
      setLastCheck(new Date());
      failureCountRef.current = 0; // Reset failure count on success

      // Call status change callback
      if (onStatusChange) {
        onStatusChange(data);
      }

      // Check if status changed and call appropriate callback
      if (previousStatus && previousStatus !== data.status) {
        if (data.status === 'ready' && onSuccess) {
          onSuccess(data);
        }
        if (data.status === 'failed' && onError) {
          onError(new Error(data.error || 'Domain provisioning failed'));
        }
      }

      setPreviousStatus(data.status);

      return data;
    } catch (err) {
      failureCountRef.current += 1;
      console.error('Error polling domain status:', err);
      setError(err.message);

      if (onError && failureCountRef.current >= 3) {
        onError(err); // Only report after 3 failures
      }

      return null;
    } finally {
      setLoading(false);
    }
  }, [club_id, domainId, onStatusChange, onSuccess, onError, previousStatus]);

  /**
   * Start polling for domain status
   */
  const startPolling = useCallback(async () => {
    if (isPollingRef.current) return;

    console.log(`Starting domain polling for ${club_id}/${domainId}`);
    isPollingRef.current = true;

    // Fetch immediately on start
    const result = await fetchDomainStatus();

    // If status is not pending, don't start interval polling
    if (result?.status && result.status !== 'pending') {
      console.log(`Domain status is ${result.status}, not starting polling interval`);
      isPollingRef.current = false;
      return;
    }

    // Set up interval polling
    pollIntervalRef.current = setInterval(async () => {
      const result = await fetchDomainStatus();

      // Stop polling if status is no longer pending
      if (result?.status && result.status !== 'pending') {
        console.log(`Domain status changed to ${result.status}, stopping polling`);
        // Clear interval directly to avoid circular dependency
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        isPollingRef.current = false;
      }
    }, interval);
  }, [club_id, domainId, fetchDomainStatus, interval]);

  /**
   * Stop polling
   */
  const stopPolling = useCallback(() => {
    console.log('Stopping domain polling');
    isPollingRef.current = false;
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  /**
   * Retry failed polling
   */
  const retry = useCallback(async () => {
    console.log('Retrying domain polling');
    failureCountRef.current = 0;
    setError(null);
    await fetchDomainStatus();
  }, [fetchDomainStatus]);

  /**
   * Auto-start polling on mount
   */
  useEffect(() => {
    if (autoStart && club_id && domainId) {
      startPolling();
    }

    return () => {
      stopPolling();
    };
  }, []); // Only run on mount/unmount

  return {
    // State
    status,
    sslStatus,
    instructions,
    dnsRecords,
    loading,
    error,
    lastCheck,
    isPolling: isPollingRef.current,

    // Controls
    startPolling,
    stopPolling,
    retry,
    refetch: fetchDomainStatus,
  };
}

export default useDomainPolling;
